<template>
  <VaLayout
    :top="{ fixed: true, order: 2 }"
    :left="{
      fixed: true,
      absolute: breakpoints.mdDown && !isSidebarMinimized,
      order: 1,
      overlay: breakpoints.mdDown && !isSidebarMinimized,
    }"
    @leftOverlayClick="isSidebarMinimized = true"
  >
    <template #top>
      <AppNavbar :is-mobile="isMobile" />
    </template>

    <template #left>
      <AppSidebar :minimized="isSidebarMinimized" :animated="!isMobile" :mobile="isMobile" />
    </template>

    <template #content>
      <main :class="isMobile ? 'p-0' : 'p-4 pt-2'">
        <article>
          <RouterView v-slot="{ Component }">
            <Suspense>
              <component :is="Component" />
              <template #fallback>
                <div class="loading-placeholder">Loading...</div>
              </template>
            </Suspense>
          </RouterView>
        </article>
      </main>
      <EventNotification />
      <NewMessageToast />
    </template>
  </VaLayout>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, computed, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import { useRoute, onBeforeRouteUpdate } from 'vue-router'
import { useBreakpoint } from 'vuestic-ui'

import { useGlobalStore } from '../stores/global-store'

import AppNavbar from '../components/navbar/AppNavbar.vue'
import AppSidebar from '../components/sidebar/AppSidebar.vue'
import EventNotification from '../components/events/EventNotification.vue'
import NewMessageToast from '../components/events/NewMessageToast.vue'

const GlobalStore = useGlobalStore()
const route = useRoute()

// Initialize reactive refs first
const sidebarWidth = ref('16rem')
const sidebarMinimizedWidth = ref(undefined)
const isMobile = ref(false)
const isTablet = ref(false)

// Get store values
const { isSidebarMinimized } = storeToRefs(GlobalStore)

// Get breakpoints after all refs are initialized
const breakpoints = useBreakpoint()

// Check if current route is chat page
const isChatPage = computed(() => route.name === 'chat')

// Define resize handler with safety checks for SSR
const onResize = () => {
  if (breakpoints) {
    isMobile.value = breakpoints.smDown
    isTablet.value = breakpoints.mdDown || isChatPage.value
    sidebarMinimizedWidth.value = isMobile.value ? '0' : '4.5rem'
    sidebarWidth.value = isTablet.value ? '100%' : '16rem'

    // Don't auto-minimize sidebar on chat page if it's not already set by route change
    if (!isChatPage.value) {
      isSidebarMinimized.value = breakpoints.mdDown
    }
  }
}

onMounted(async () => {
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', onResize)
    await nextTick()
    onResize()
  }
})

const handleRouteUpdate = async (to) => {
  await nextTick()
  // Collapse sidebar after route change for Mobile or Chat page
  if (breakpoints.mdDown || to.name === 'chat') {
    isSidebarMinimized.value = true
  }
}

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', onResize)
  }
})

onBeforeRouteUpdate(handleRouteUpdate)
</script>

<style lang="scss" scoped>
// Prevent icon jump on animation
.va-sidebar {
  width: unset !important;
  min-width: unset !important;
}

// Add loading placeholder
.loading-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  font-size: 1.2rem;
  color: var(--va-primary);
}
</style>
